/* eslint-disable react/no-array-index-key */
/* eslint-disable no-void */
import React from 'react';
import {
  Box,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { MinusCircle } from 'lucide-react';

import type { SerialRow, SerialRowMap } from './cartTypes';
import { cartItemKey } from './cartItemKey';
import { isCrossgradeProduct } from './cartLineKinds';
import { rowHasSerialOrHost } from './upgradeSerialMapPayload';

export interface UpgradeSerialEditorProps {
  item: any;
  rows: SerialRow[];
  setSerialRowsByItem: React.Dispatch<React.SetStateAction<SerialRowMap>>;
  validateSerialForItem: (
    item: any,
    rowIndex: number,
    rawSerial: string,
    targetHostAppIdOverride?: string,
  ) => void | Promise<void>;
  removeSerialRow: (item: any, rowIndex: number) => void;
  commitUpgradeSerials: (item: any, rows: SerialRow[]) => void;
}

export function UpgradeSerialEditor({
  item,
  rows,
  setSerialRowsByItem,
  validateSerialForItem,
  removeSerialRow,
  commitUpgradeSerials,
}: UpgradeSerialEditorProps) {
  const isCrossgrade = isCrossgradeProduct(item.productId);

  return (
    <Box
      sx={{
        paddingLeft: '155px',
        borderRadius: 2,
        width: 'calc(100% + 160px)',
        ml: { sm: '-140px' },
        mt: { sm: '-32px' },
      }}
    >
      <Typography variant="subtitle2" sx={{ color: '#666', mt: 3 }}>
        Enter current serial information to complete checkout
      </Typography>

      {rows.map((row, idx) => (
        <Box
          key={idx}
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '2fr 1.3fr auto' },
            gap: 1,
            alignItems: 'start',
            mb: 1,
            pt: 1,
          }}
        >
          <TextField
            label={`Serial #${idx + 1}`}
            fullWidth
            size="small"
            value={row.serial}
            onChange={(e) => {
              const raw = e.target.value;
              const thisKey = cartItemKey(item);

              setSerialRowsByItem((prev) => {
                const currentRows = prev[thisKey] || [];
                const nextRows = [...currentRows];

                nextRows[idx] = {
                  ...nextRows[idx],
                  serial: raw,
                  ...(raw.trim() === ''
                    ? { status: 'idle' as const, error: undefined }
                    : {}),
                };

                return { ...prev, [thisKey]: nextRows };
              });

              if (raw.trim() === '') return;

              void validateSerialForItem(item, idx, raw);
            }}
            error={row.status === 'invalid'}
            helperText={row.error}
            sx={{ backgroundColor: 'white' }}
          />

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <FormControl sx={{}}>
              <InputLabel id={`hostapp-label-${idx}`}>
                Host Application
              </InputLabel>

              <Select
                labelId={`hostapp-label-${idx}`}
                label="Host Application"
                size="small"
                value={
                  isCrossgrade
                    ? row.targetHostApp?.id ?? ''
                    : row.hostApp?.id ?? ''
                }
                onChange={(e) => {
                  const selected =
                    item.productId.supportedHostApplications.find(
                      (app: any) => app.id === e.target.value,
                    ) || null;

                  const thisKey = cartItemKey(item);
                  let nextRowsSnapshot: SerialRow[] = [];

                  setSerialRowsByItem((prev) => {
                    const next = { ...prev };
                    const rowsCopy = [...(next[thisKey] || [])];

                    rowsCopy[idx] = {
                      ...rowsCopy[idx],
                      ...(isCrossgrade
                        ? { targetHostApp: selected }
                        : { hostApp: selected }),
                    };

                    next[thisKey] = rowsCopy;
                    nextRowsSnapshot = rowsCopy;
                    return next;
                  });

                  const changedRow = nextRowsSnapshot[idx];
                  if (isCrossgrade && changedRow?.serial?.trim()) {
                    // Validate before PATCH so refetch does not clear same-host errors
                    void validateSerialForItem(
                      item,
                      idx,
                      changedRow.serial,
                      selected?.id,
                    );
                  } else if (rowHasSerialOrHost(changedRow, item)) {
                    // Upgrade: host-only rows must PATCH to split SINGLE vs ALL tiers.
                    // Crossgrade without serial: same (commit; validate when serial exists).
                    commitUpgradeSerials(item, nextRowsSnapshot);
                  }
                }}
                displayEmpty
                sx={{
                  minWidth: 160,
                  backgroundColor: 'white',
                }}
              >
                {(item.productId.supportedHostApplications || []).map(
                  (app: any) => (
                    <MenuItem key={app.id} value={app.id}>
                      {app.displayName}
                    </MenuItem>
                  ),
                )}
              </Select>
            </FormControl>
          </Box>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: { xs: 'flex-start', sm: 'flex-end' },
              gap: 1,
            }}
          >
            <Tooltip title="Remove this serial row" arrow>
              <IconButton
                size="small"
                onClick={() => removeSerialRow(item, idx)}
              >
                <MinusCircle color="black" size={18} />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      ))}
    </Box>
  );
}
